import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumb } from "@/components/Breadcrumb";
import { StructuredData } from "@/components/StructuredData";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Dices, Sparkles, BookOpen, Users, Calculator, Award } from "lucide-react";
import educationPrimaryHero from "@/assets/education-primary-hero.jpg";

const EducationPrimary = () => {
  const breadcrumbs = [
    { label: "Education", href: "/education/primary" },
    { label: "Primary School", href: "/education/primary" }
  ];

  return (
    <Layout>
      <SEO
        title="RandomGen Numbers Generator for Primary Schools - Free Educational Math Tools"
        description="Free random number generator tools for elementary education. Teach probability, counting, and basic mathematics to students aged 5-11 with interactive games and classroom activities."
        canonical="https://numbers-generator.com/education/primary"
        keywords="primary school random generator, elementary math tools, teaching probability, classroom activities, educational number generator"
      />
      <StructuredData 
        type="BreadcrumbList"
        breadcrumbs={[
          { name: "Home", url: "https://numbers-generator.com/" },
          { name: "Education", url: "https://numbers-generator.com/education/primary" },
          { name: "Primary School", url: "https://numbers-generator.com/education/primary" }
        ]}
      />
      <div className="container mx-auto px-4 py-12">
        <Breadcrumb items={breadcrumbs} />
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Numbers Generator for Primary Schools
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Free educational tools for teaching probability, counting, and basic mathematics to elementary students. 
            Make learning fun with interactive random number games and activities.
          </p>
        </header>

        {/* Key Benefits */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <BookOpen className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Interactive Learning</h3>
              <p className="text-muted-foreground">
                Engage young students with colorful dice rollers, coin flips, and number spinners that make math concepts tangible and exciting.
              </p>
            </Card>
            <Card className="p-6">
              <Users className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-semibold mb-2">Classroom Activities</h3>
              <p className="text-muted-foreground">
                Perfect for group activities, team selection, turn-taking games, and fair decision-making in elementary classrooms.
              </p>
            </Card>
            <Card className="p-6">
              <Calculator className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Number Recognition</h3>
              <p className="text-muted-foreground">
                Help students aged 5-11 practice counting, number identification, greater-than/less-than concepts, and basic arithmetic.
              </p>
            </Card>
          </div>
        </section>

        {/* Lesson Plan Ideas */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Primary School Lesson Ideas</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Award className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Math Warm-Up Activities (Ages 5-7)</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Number of the Day:</strong> Generate a random number 1-20; students count objects, draw dots, or write the number</li>
                <li>• <strong>Greater Than / Less Than:</strong> Generate two numbers; students use &lt; &gt; = symbols to compare</li>
                <li>• <strong>Skip Counting Practice:</strong> Generate multiples of 2, 5, or 10 for pattern recognition</li>
                <li>• <strong>Dice Addition:</strong> Roll virtual dice; students practice mental addition with visual support</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Sparkles className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Probability & Prediction (Ages 8-11)</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Coin Flip Experiments:</strong> Track heads vs tails outcomes; introduce fairness concepts</li>
                <li>• <strong>Likely vs Unlikely:</strong> Predict if random number will be odd/even before generating</li>
                <li>• <strong>Dice Probability:</strong> Roll virtual dice 20 times; create bar graphs of results</li>
                <li>• <strong>Random Number Range:</strong> Explore different ranges (1-10, 1-100) and discuss probability</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Dices className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Classroom Management Tools</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Fair Team Selection:</strong> Generate random numbers to divide class into groups</li>
                <li>• <strong>Student Selector:</strong> Assign each student a number; generator picks who answers questions</li>
                <li>• <strong>Turn-Taking for Games:</strong> Use dice roller to determine who goes first</li>
                <li>• <strong>Random Act of Kindness:</strong> Pick random students to help with classroom tasks</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <BookOpen className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Cross-Curricular Activities</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Story Starters:</strong> Generate random numbers corresponding to story prompts or word lists</li>
                <li>• <strong>PE Activities:</strong> Random number determines jumping jacks, exercises, or activity duration</li>
                <li>• <strong>Art Projects:</strong> Use random numbers to select colors, shapes, or drawing subjects</li>
                <li>• <strong>Science Experiments:</strong> Randomly select variables for simple experiments</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tools Section */}
        <section className="mb-16 bg-card/30 backdrop-blur-sm rounded-lg p-8 border border-border/50">
          <h2 className="text-3xl font-bold text-center mb-6">Free Tools for Primary Education</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/games">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Dices className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Dice Roller</h3>
                <p className="text-muted-foreground">Virtual dice for addition practice, probability lessons, and game-based learning</p>
              </Card>
            </Link>
            <Link to="/games">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Sparkles className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-semibold mb-2">Coin Flip</h3>
                <p className="text-muted-foreground">Teach probability, fairness, and yes/no decision making with virtual coin tosses</p>
              </Card>
            </Link>
            <Link to="/">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Calculator className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Number Generator</h3>
                <p className="text-muted-foreground">Generate numbers in any range for counting, comparison, and arithmetic exercises</p>
              </Card>
            </Link>
          </div>
        </section>

        {/* Educational Standards Alignment */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Curriculum Standards Alignment</h2>
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm rounded-lg p-8 border border-border/50">
            <p className="text-muted-foreground mb-6">
              Our random number generator tools support key mathematics standards for primary education:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-lg mb-3">Common Core Math Standards</h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• K.CC.A.1 - Count to 100</li>
                  <li>• 1.NBT.B.2 - Understanding place value</li>
                  <li>• 2.MD.D.10 - Data representation</li>
                  <li>• 3.OA.A.1 - Multiplication concepts</li>
                  <li>• 4.NF.A.1 - Fraction equivalents</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3">UK National Curriculum</h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• Year 1: Number and place value</li>
                  <li>• Year 2: Addition and subtraction</li>
                  <li>• Year 3: Multiplication tables</li>
                  <li>• Year 4: Probability (likely/unlikely)</li>
                  <li>• Year 5: Statistics and data handling</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Start Using Free Random Number Tools Today</h2>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            No registration required. All tools are completely free for teachers, parents, and homeschoolers.
          </p>
          <Link to="/">
            <Button size="lg" className="text-lg px-8">
              Try Number Generator Now
            </Button>
          </Link>
        </section>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={educationPrimaryHero} 
            alt="Primary school classroom with young students learning mathematics, colorful numbers, and educational activities using random number generator tools"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default EducationPrimary;